/**
 * Error reports → GlitchTip (T11-06, T00-13). GlitchTip speaks the Sentry protocol, so the
 * MIT-licensed `@sentry/react` SDK is pointed at the GlitchTip DSN; it is loaded lazily and only
 * when a DSN is configured (no cost for the first paint, nothing sent in dev by default).
 *
 * What is attached: the `requestId` of the failing API call (to find the service log line),
 * the route, the staff id. No names, e-mails, addresses or form contents (APPI, design/08).
 */
import type * as SentryModule from "@sentry/react";

import { env } from "@/lib/env";

export interface ErrorContext {
  route?: string;
  staffId?: string | null;
  requestId?: string | null;
  code?: string;
}

type Sdk = typeof SentryModule;

let sdk: Promise<Sdk | null> | null = null;

/** Keys that may carry personal data — dropped from every event before it leaves the browser. */
const PII_KEYS = /(email|name|phone|address|password|token|passport|birth|account)/i;

function scrub(value: unknown, depth = 0): unknown {
  if (depth > 4 || value === null || typeof value !== "object") {
    return value;
  }
  if (Array.isArray(value)) {
    return value.map((v) => scrub(v, depth + 1));
  }
  const out: Record<string, unknown> = {};
  for (const [key, child] of Object.entries(value as Record<string, unknown>)) {
    out[key] = PII_KEYS.test(key) ? "[removed]" : scrub(child, depth + 1);
  }
  return out;
}

export function initMonitoring(dsn: string = env.VITE_GLITCHTIP_DSN): Promise<Sdk | null> {
  if (!dsn) {
    return Promise.resolve(null);
  }
  sdk ??= import("@sentry/react")
    .then((Sentry) => {
      Sentry.init({
        dsn,
        release: env.VITE_RELEASE,
        environment: import.meta.env.MODE,
        // GlitchTip: errors only (no performance/replay products)
        tracesSampleRate: 0,
        beforeSend(event) {
          if (event.user) {
            event.user = event.user.id ? { id: event.user.id } : {};
          }
          if (event.request) {
            delete event.request.cookies;
            delete event.request.data;
          }
          if (event.extra) {
            event.extra = scrub(event.extra) as Record<string, unknown>;
          }
          return event;
        },
      });
      return Sentry;
    })
    .catch(() => null);
  return sdk;
}

/** Report an unexpected error with the admin context (fire-and-forget). */
export function reportError(error: unknown, context: ErrorContext = {}): void {
  if (import.meta.env.DEV || import.meta.env.MODE === "test") {
    console.error("[admin]", error, context);
  }
  void (sdk ?? initMonitoring()).then((Sentry) => {
    if (!Sentry) {
      return;
    }
    Sentry.withScope((scope) => {
      if (context.route) {
        scope.setTag("route", context.route);
      }
      if (context.requestId) {
        scope.setTag("request_id", context.requestId);
      }
      if (context.code) {
        scope.setTag("error_code", context.code);
      }
      if (context.staffId) {
        scope.setUser({ id: context.staffId });
      }
      Sentry.captureException(error);
    });
  });
}

export interface FeedbackReport {
  kind: "problem" | "idea";
  /** What the staff member typed (they are asked not to type customers' details). */
  message: string;
  route: string;
  staffId?: string | null;
  requestId?: string | null;
}

/**
 * "Report a problem or idea" (T11-47) → GlitchTip as an `info` event tagged `feedback` (GlitchTip
 * files it as an issue of the admin project, with the route, the staff id and the last failed
 * `requestId`). `false` when no DSN is configured or the SDK cannot load — the dialog then offers
 * e-mail.
 */
export async function sendFeedback(
  report: FeedbackReport,
  dsn: string = env.VITE_GLITCHTIP_DSN,
): Promise<boolean> {
  if (!dsn) {
    return false;
  }
  const Sentry = await (sdk ?? initMonitoring(dsn));
  if (!Sentry) {
    return false;
  }
  Sentry.withScope((scope) => {
    scope.setLevel("info");
    scope.setTag("feedback", report.kind);
    scope.setTag("route", report.route);
    if (report.requestId) {
      scope.setTag("request_id", report.requestId);
    }
    if (report.staffId) {
      scope.setUser({ id: report.staffId });
    }
    scope.setExtra("feedback_message", report.message.slice(0, 4000));
    Sentry.captureMessage(`Staff feedback (${report.kind}): ${report.message.slice(0, 120)}`);
  });
  return true;
}
